import { useState } from "react";
import { CreateNewToolId, useCreateNewTool } from "./Dialogs/useCreateNewTool";
import { useImportTool } from "./Dialogs/useImportTool";
import { ButtonGroup } from "./shadcn/button-group";
import { Button } from "./shadcn/button";
import {
  MoreHorizontalIcon,
  Plus,
  Workflow,
  Bot,
  BookOpen,
  Table2,
  Upload,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./shadcn/dropdown-menu";
import { SpotToast } from "./SpotToast";
import { useSpotUser } from "@/utils/spotUser";
import { usePermissions } from "@/hooks/usePermissions";
import { P } from "@/constants/permissions";

type tabValues = "flows" | "agents" | "knowledge-bases" | "tables";

const tabCreateButtonMap: Record<
  tabValues,
  { toolId: CreateNewToolId; label: string }
> = {
  flows: {
    toolId: "agentic-workflow",
    label: "Create Flow",
  },
  agents: {
    toolId: "co-worker",
    label: "Create Agent",
  },
  "knowledge-bases": {
    toolId: "knowledge-base",
    label: "Create Knowledge Base",
  },
  tables: {
    toolId: "table-tool",
    label: "Create Table",
  },
};

type MenuItemId = "flow" | "agent" | "kb" | "table" | "import";

export const CreateToolButton = ({
  tab,
  isDisabled,
  allowedItems,
}: {
  tab: string;
  isDisabled?: boolean;
  /** When provided, the dropdown only shows the listed item types. */
  allowedItems?: MenuItemId[];
}) => {
  const { spotGroup } = useSpotUser();
  // When Editor V2 is enabled for the workspace, the primary "Create Flow"
  // action creates a V2 flow and the explicit "Create V2 Flow" menu item is
  // dropped (it would be redundant).
  const editorV2Enabled = Boolean(spotGroup.settings?.editor_v2_enabled);
  const { hasPermission } = usePermissions();
  // Hide the create entry point entirely when the user can't actually
  // create. Server still 403s if they bypass the UI, but pre-flight
  // hiding avoids the misleading "There was an error..." path.
  const canCreate = hasPermission(P.RESOURCE_EDIT, "group");

  const [isCreating, setIsCreating] = useState<boolean>(false); // to prevent multiple clicks while pushing router change
  const [isCreatingFromMenu, setIsCreatingFromMenu] = useState<boolean>(false);
  const { createNewTool } = useCreateNewTool({
    // Creating a table opens a dialog instead of navigating, so the buttons
    // have to come back out of their loading state.
    onCreated: () => {
      setIsCreating(false);
      setIsCreatingFromMenu(false);
    },
    onError: () => setIsCreating(false),
  });
  const { openFilePicker, isImporting, fileInput } = useImportTool();

  const primaryToolId: CreateNewToolId | undefined =
    tabCreateButtonMap[tab as tabValues]?.toolId;

  const handleClick = () => {
    if (primaryToolId) {
      setIsCreating(true);
      createNewTool(primaryToolId);
    } else {
      SpotToast.error("Invalid tool type");
      setIsCreating(false);
    }
  };

  const handleCreateFromMenu = (toolId: CreateNewToolId) => {
    setIsCreatingFromMenu(true);
    createNewTool(toolId);
  };

  if (!canCreate) {
    return null;
  }

  const createItems: {
    id: MenuItemId;
    label: string;
    icon: LucideIcon;
    onSelect: () => void;
    hidden?: boolean;
  }[] = [
    {
      id: "flow",
      label: "Create Flow",
      icon: Workflow,
      onSelect: () => handleCreateFromMenu("agentic-workflow"),
      hidden: tab === "flows" && editorV2Enabled,
    },
    {
      id: "agent",
      label: "Create Agent",
      icon: Bot,
      onSelect: () => handleCreateFromMenu("co-worker"),
    },
    {
      id: "kb",
      label: "Create Knowledge Base",
      icon: BookOpen,
      onSelect: () => handleCreateFromMenu("knowledge-base"),
    },
    {
      id: "table",
      label: "Create Table",
      icon: Table2,
      onSelect: () => handleCreateFromMenu("table-tool"),
    },
  ];
  const visibleCreateItems = createItems.filter(
    (item) => !item.hidden && (!allowedItems || allowedItems.includes(item.id))
  );
  const showImport = !allowedItems || allowedItems.includes("import");

  return (
    <>
      <ButtonGroup>
        <Button
          loading={isCreating}
          disabled={isCreatingFromMenu || isImporting || isDisabled}
          onClick={handleClick}
        >
          <Plus />
          {tabCreateButtonMap[tab as tabValues]?.label || "Create Tool"}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              aria-label="More Options"
              disabled={isCreating || isDisabled}
              loading={isCreatingFromMenu || isImporting}
            >
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-52 whitespace-nowrap"
          >
            {visibleCreateItems.length > 0 && (
              <DropdownMenuGroup>
                {visibleCreateItems.map((item) => (
                  <DropdownMenuItem key={item.id} onClick={item.onSelect}>
                    <item.icon color="var(--primary)" strokeWidth={1.25} />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            )}
            {visibleCreateItems.length > 0 && showImport && (
              <DropdownMenuSeparator />
            )}
            {showImport && (
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={openFilePicker}>
                  <Upload color="var(--primary)" strokeWidth={1.25} />
                  Import
                </DropdownMenuItem>
              </DropdownMenuGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
      {fileInput}
    </>
  );
};
